/**
 * offlineSync.js — processes the offline queue when connectivity is restored.
 * Attach event listeners in App.js or a top-level component.
 */
import axios from 'axios';
import { offlineDB } from './offlineDB';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let isSyncing = false;
const listeners = new Set();

/** Notify all subscribed UI components of pending count changes. */
function emit(pendingCount) {
  listeners.forEach((fn) => fn(pendingCount));
}

export function onSyncUpdate(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Process one entry from the offline queue. */
async function processEntry(entry) {
  const token = localStorage.getItem('token');
  if (!token) return false;

  const method = (entry.method || 'POST').toLowerCase();
  const url = `${API}${entry.endpoint}`;

  const response = await axios[method](url, entry.data, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 15000,
  });
  return response.data;
}

/** Sync all pending entries. Call when device comes online. */
export async function syncPendingEntries(onProgress) {
  if (isSyncing) return;
  if (!navigator.onLine) return;

  isSyncing = true;
  let synced = 0;
  let failed = 0;

  try {
    const pending = await offlineDB.getPending();
    if (pending.length === 0) {
      isSyncing = false;
      return { synced: 0, failed: 0 };
    }

    for (const entry of pending) {
      try {
        const serverData = await processEntry(entry);
        await offlineDB.markSynced(entry.offlineId, serverData);
        synced++;
        onProgress?.({ type: 'synced', entry, synced, total: pending.length });
      } catch (err) {
        const errMsg =
          err.response?.data?.detail ||
          err.message ||
          'Unknown error';
        await offlineDB.markFailed(entry.offlineId, errMsg);
        failed++;
        onProgress?.({ type: 'failed', entry, error: errMsg });
      }
    }

    // Update pending count for UI
    const remaining = await offlineDB.pendingCount();
    emit(remaining);

    // Clean up old synced entries
    await offlineDB.cleanup();
  } finally {
    isSyncing = false;
  }

  return { synced, failed };
}

/** Get current pending count. */
export async function getPendingCount() {
  try {
    return await offlineDB.pendingCount();
  } catch {
    return 0;
  }
}

/** Boot: attach window online listener and do initial sync. */
export function initOfflineSync(onPendingCountChange) {
  if (onPendingCountChange) {
    listeners.add(onPendingCountChange);
  }

  // Initial count
  getPendingCount().then((count) => emit(count));

  // Auto-sync when online
  const handleOnline = async () => {
    const result = await syncPendingEntries();
    if (result && result.synced > 0) {
      // Trigger a page refresh signal so data tables re-fetch
      window.dispatchEvent(new CustomEvent('offlineSyncComplete', { detail: result }));
    }
    const count = await getPendingCount();
    emit(count);
  };

  window.addEventListener('online', handleOnline);
  return () => window.removeEventListener('online', handleOnline);
}
